/**
 * VT Digital Theme JavaScript
 * Modular architecture with conditional loading
 */

// Import all modules
import { initSwipers } from './modules/swipers';
import { initAllAccordions } from './modules/accordions';
import { initTestimonialsTabs, initTestimonialsLoadMore } from './modules/testimonials';
import { initHeaderScripts } from './modules/header';
import { initBlogFeatures } from './modules/blog';
import { initBlogFilter, initCategoryFilter, initAuthorFilter } from './modules/blog-filter';
import { initPortfolioFilter } from './modules/portfolio-filter';
import { initVideoModal } from './modules/video-modal';
import { initServiceCards } from './modules/service-cards';
import { initVTContainerSpacing, initScrollToTop } from './modules/utils';
import { initLazyVideos } from './modules/lazy-videos';
import { initProgressBarCustom } from './modules/progress-bar-custom';
import { initAttributionCF7 } from './modules/attribution-cf7';
import { initCF7PhoneInput } from './modules/cf7-phone';
import { initBlogPdfDownload } from './modules/pdf-download';
import { initAppFeaturesTabs } from './modules/app-features-tabs';

/**
 * Initialize all features with conditional loading
 * Only init features when their elements exist on the page
 */
function initApp() {
    // ============================================
    // ALWAYS LOAD - Core features needed on all pages
    // ============================================
    initHeaderScripts();
    initVTContainerSpacing();
    initScrollToTop();
    
    // Lazy Videos - always init (defers video loading until near viewport)
    initLazyVideos();

    // ============================================
    // CONDITIONAL LOADING - Only init when elements exist
    // ============================================

    // Swipers - only if any swiper element exists
    if (document.querySelector('.swiper')) {
        initSwipers();
    }

    // Accordions - only if accordion elements exist
    if (document.querySelector('.accordion-item, .faq-button, .tech-accordion-item, .accordion-button[data-tab]')) {
        initAllAccordions();
    }

    // Testimonials - tabs and load more
    if (document.querySelector('.testimonial-tab-btn')) {
        initTestimonialsTabs();
    }
    
    // Testimonials Load More - for page-testimonials.php
    if (document.querySelector('.btn-load-more[data-type]')) {
        initTestimonialsLoadMore();
    }

    // Video Modal - only if video modal exists
    if (document.getElementById('videoModal')) {
        initVideoModal();
    }

    // Blog Features - only on single blog posts
    if (
        document.getElementById('reading-progress') ||
        document.querySelector('.toc-link') ||
        document.querySelector('.js-audio-toggle') ||
        document.querySelector('[data-custom-audio-player]')
    ) {
        initBlogFeatures();
    }

    // Blog Filter - only on blog listing page
    if (document.getElementById('blog-posts-container')) {
        initBlogFilter();
    }

    // Category Filter - only on category archive page
    if (document.getElementById('category-posts-container')) {
        initCategoryFilter();
    }

    // Author Filter - only on author archive page
    if (document.getElementById('author-posts-container')) {
        initAuthorFilter();
    }

    // Portfolio Filter - only on portfolio archive page
    if (document.getElementById('portfolio-args')) {
        initPortfolioFilter();
    }

    // Service Cards - only on service single page
    if (document.getElementById('service-cards-container')) {
        initServiceCards();
    }

    // Progress Bar Custom - only on pages with services scrollbar section
    if (document.querySelector('.services-card-item-wrapper')) {
        initProgressBarCustom();
    }

    // Blog PDF Download - only when the gated PDF section is present
    if (document.querySelector('.blog-pdf-download')) {
        initBlogPdfDownload();
    }

    // App Features Tabs - only when the apps/panels tabs section is present
    if (document.querySelector('.app-features-tabs')) {
        initAppFeaturesTabs();
    }

    // Contact Form 7 - reset status on input + marketing attribution (traffic_details)
    if (document.querySelector('.wpcf7')) {
        initCF7Reset();
        initCF7PhoneInput();
    }
    if (
        document.querySelector('.wpcf7') ||
        document.querySelector('input[name="traffic_details"], input#traffic_details')
    ) {
        initAttributionCF7();
    }
}

/**
 * Contact Form 7 - Reset form status on input
 */
function initCF7Reset() {
    const inputs = document.querySelectorAll('.wpcf7 input, .wpcf7 textarea, .wpcf7 select');
    inputs.forEach(function(input) {
        input.addEventListener('input', function() {
            const parentForm = input.closest('form');
            if (parentForm) {
                parentForm.className = '';
                parentForm.classList.add('wpcf7-form', 'init');
                parentForm.setAttribute('data-status', 'init');
            }
        });
    });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}
